import { createContext, useContext, useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { DashboardSseProvider } from "../../context/DashboardSseContext.js";
import { LifecycleEventToast } from "../notifications/LifecycleEventToast.js";
import { ItdrAlertToaster } from "../itdr/ItdrAlertToaster";
import { PrimarySidebar, PRIMARY_SIDEBAR_WIDTH } from "./PrimarySidebar";
import { SecondaryNav, SECONDARY_NAV_WIDTH } from "./SecondaryNav";
import { ContentHeader } from "./ContentHeader";
import { TenantBranding } from "./TenantBranding";

const CONTENT_LEFT_OFFSET = PRIMARY_SIDEBAR_WIDTH + SECONDARY_NAV_WIDTH; // 80 + 220 = 300px

/**
 * Context used by embedded `<TopBar />` components to detect that AppLayout's
 * `ContentHeader` already owns the global chrome (search / Cmd-K,
 * notifications, density, tenant chip, account menu). Inside AppLayout,
 * TopBar renders only page-header content — title, subtitle, page-local
 * actions — or nothing at all when a page passes none (RFC-004 R5.6).
 */
const AppLayoutCtx = createContext(false);
export const useIsInsideAppLayout = () => useContext(AppLayoutCtx);

/**
 * Sidebar collapse context — allows PrimarySidebar to toggle the SecondaryNav
 * visibility and AppLayout to adjust the content margin.
 */
interface SidebarCollapseCtx {
  collapsed: boolean;
  toggle: () => void;
}
const SidebarCollapseContext = createContext<SidebarCollapseCtx>({
  collapsed: false,
  toggle: () => undefined,
});
export const useSidebarCollapse = () => useContext(SidebarCollapseContext);

const SIDEBAR_COLLAPSED_KEY = "ia-sidebar-collapsed";

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      /* localStorage unavailable */
    }
  }, [collapsed]);

  const contentOffset = collapsed
    ? PRIMARY_SIDEBAR_WIDTH
    : CONTENT_LEFT_OFFSET;

  return (
    <AppLayoutCtx.Provider value={true}>
    <SidebarCollapseContext.Provider value={{ collapsed, toggle: () => setCollapsed((c) => !c) }}>
    <DashboardSseProvider>
      <div className="min-h-screen bg-app text-app transition-colors duration-300">
        <TenantBranding />

        {/* Primary Sidebar (Icon Rail) */}
        <PrimarySidebar />

        {/* Secondary Navigation (Contextual) — hidden when collapsed */}
        {!collapsed && <SecondaryNav />}

        {/* Main Content Area */}
        <div
          className="min-h-screen flex flex-col transition-[margin] duration-300"
          style={{ marginLeft: contentOffset }}
        >
          {/* Content Header (Breadcrumbs + Search + Notifications) */}
          <ContentHeader />

          {/* Page Content — max-width container + section-gap for density-compact */}
          <main id="main-content" className="flex-1 p-6">
            <LifecycleEventToast />
            <div className="mx-auto w-full max-w-screen-2xl" style={{ display: "flex", flexDirection: "column", gap: "var(--section-gap)" }}>
              <Outlet />
            </div>
          </main>
        </div>

        {/* Phase 6 ITDR: real-time alert toasts wired via Redis pub/sub SSE. */}
        <ItdrAlertToaster />
      </div>
    </DashboardSseProvider>
    </SidebarCollapseContext.Provider>
    </AppLayoutCtx.Provider>
  );
}

// Keep backward-compatible exports for any code referencing old sidebar widths
export const SIDEBAR_WIDTH_EXPANDED = CONTENT_LEFT_OFFSET;
export const SIDEBAR_WIDTH_COLLAPSED = PRIMARY_SIDEBAR_WIDTH;
